import { ClientNotification } from './client-notification';
import { NotificationConnection } from './notification-connection';
import { NotificationLinkType } from './notification-link-type';
import { NotificationState } from './notification-state';

export interface ClientNotificationLink {
    /**
     * The success link to navigate to the object view. (optional)
     * At default, it brings to the home page of the module.
     * The path is relative to from the manifest opened point unless linkType is specified
     */
    link: string;

    /**
     * The text to show up on the notification link text ex: "Go to <linkText> on sme-xyz.domain.com"
     * By default the text will be the auto detected source name like "Files"
     */
    linkText?: string;

    /**
     * the type of notification link. Default behavior is RelativeToTool
     */
    linkType?: NotificationLinkType;
}

/**
 * Utility class to manage client notification
 */
export class ClientNotificationInstance {
    private notification: ClientNotification;

    /**
     * Creates a new ClientNotificationInstance
     * @param notificationConnection reference to NotificationConnection, can be found at AppContextService.notification
     * @param nodeName the node name for the notification
     */
    constructor(private notificationConnection: NotificationConnection, private nodeName: string) {
        this.notification = {
            id: MsftSme.newGuid(),
            state: NotificationState.InProgress,
            title: '',
            message: ''
        };
    }

    /**
     * Gets the notification ID to recycle.
     * Sets this ID to the Work Item ID to share the notification.
     */
    public get id(): string {
        return this.notification.id;
    }

    /**
     * Shows an in progress notification
     * This should be used for all started, submitted, in progress, or status update notifications
     * @param title localized title for the notification. Should be like "Executing some action"
     * @param message localized message for the notification. Should be natural language like "Executing some action on target 'name'"
     * @param link optional custom link for notification
     */
    public showInProgress(title: string, message: string, link?: ClientNotificationLink) {
        this.showNotification(title, message, link, NotificationState.InProgress);
    }

    /**
     * Shows a critical notification
     * @param title localized title for the notification.
     * @param message localized message for the notification
     * @param link optional custom link for notification
     * @param solutionMessage optional solution message to address error
     */
    public showCritical(title: string, message: string, link?: ClientNotificationLink, solutionMessage?: string) {
        this.showNotification(title, message, link, NotificationState.Critical, solutionMessage);
    }

    /**
     * Shows an error notification
     * @param title localized title for the notification. Should be like "Failed to execute some action"
     * @param message localized message for the notification.
     * Should be natural language like "Failed to execute some action on target 'name'. Error: <error message>"
     * @param link optional custom link for notification
     * @param solutionMessage optional solution message to address error
     */
    public showError(title: string, message: string, link?: ClientNotificationLink, solutionMessage?: string) {
        this.showNotification(title, message, link, NotificationState.Error, solutionMessage);
    }

    /**
     * Shows a warning notification
     * @param title localized title for the notification.
     * @param message localized message for the notification
     * @param link optional custom link for notification
     */
    public showWarning(title: string, message: string, link?: ClientNotificationLink) {
        this.showNotification(title, message, link, NotificationState.Warning);
    }

    /**
     * Shows a success notification
     * @param title localized title for the notification. Should be like "Successfully executed some action"
     * @param message localized message for the notification.
     * Should be natural language like "Successfully executed some action on target 'name'"
     * @param link optional custom link for notification
     */
    public showSuccess(title: string, message: string, link?: ClientNotificationLink) {
        this.showNotification(title, message, link, NotificationState.Success);
    }

    /**
     * Shows an informational notification
     * If the information is relative to an action currently in progress or already started, use showInProgressNotification
     * @param title localized title for the notification.
     * @param message localized message for the notification
     * @param link optional custom link for notification
     */
    public showInformation(title: string, message: string, link?: ClientNotificationLink) {
        this.showNotification(title, message, link, NotificationState.Informational);
    }

    /**
     * show notification of given state
     * @param title the localized title
     * @param message the localized message
     * @param link the link
     * @param state the state
     * @param solutionMessage optional solution message for error or critical notification
     */
    private showNotification(
        title: string,
        message: string,
        link: ClientNotificationLink,
        state: NotificationState,
        solutionMessage?: string) {

        if (MsftSme.isNullOrWhiteSpace(title) || MsftSme.isNullOrWhiteSpace(message)) {
            throw new Error('Notification title and message must be defined and nonempty');
        }

        this.notification.state = state;
        this.notification.title = title;
        this.notification.message = message;
        this.notification.solutionMessage = solutionMessage ? solutionMessage : null;

        this.notification.link = link ? link.link : null;
        this.notification.linkText = link ? link.linkText : null;
        this.notification.linkType = link ? link.linkType : null;

        this.notificationConnection.notify(this.nodeName, this.notification);
    }
}
